import React, { useState } from 'react';
import { Bell, CheckCircle, X, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stock Alert Button and Modal - supports both "Back in Stock" and "Pre-Order" notifications
const StockAlert = ({ product, variant = 'auto' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [notificationType, setNotificationType] = useState('restock'); // 'restock' or 'preorder'

  // Determine alert type based on product status
  const isOutOfStock = !product.in_stock;
  const isLowStock = product.in_stock && product.stock_quantity !== null && 
                     product.stock_quantity <= (product.low_stock_threshold || 5) && 
                     product.stock_quantity > 0;
  
  // Auto-determine variant if set to 'auto'
  const alertVariant = variant === 'auto' 
    ? (isOutOfStock ? 'restock' : 'preorder') 
    : variant;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/stock-alerts`, {
        email,
        name,
        product_id: product.id,
        alert_type: notificationType
      });
      setSubmitted(true);
      if (notificationType === 'preorder') {
        toast.success("You'll be notified when pre-orders open!");
      } else {
        toast.success("You'll be notified when this product is back in stock!");
      }
    } catch (error) {
      console.error('Stock alert error:', error);
      if (error.response?.data?.detail?.includes('already')) {
        toast.info("You're already on the notification list!");
        setSubmitted(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // If product is in stock and not low stock, don't show this component (unless force showing for preorder)
  if (product.in_stock && !isLowStock && variant === 'auto') return null;

  const buttonText = alertVariant === 'preorder' 
    ? 'Join Pre-Order Waitlist' 
    : 'Notify Me When Available';
  
  const modalTitle = alertVariant === 'preorder'
    ? 'Pre-Order Notification'
    : 'Out of Stock';
    
  const modalDescription = alertVariant === 'preorder'
    ? `Enter your email to be notified when pre-orders open for`
    : `Enter your email and we'll notify you when`;

  const successMessage = alertVariant === 'preorder'
    ? "We'll email you as soon as pre-orders open for"
    : "We'll email you as soon as";

  return (
    <>
      {/* Notify Me Button */}
      <Button
        onClick={() => setIsOpen(true)}
        variant="outline"
        className={`w-full py-6 text-lg ${
          alertVariant === 'preorder' 
            ? 'border-[#003456] text-[#003456] hover:bg-[#003456] hover:text-white'
            : 'border-[#BC4749] text-[#BC4749] hover:bg-[#BC4749] hover:text-white'
        }`}
        data-testid="notify-me-btn"
      >
        {alertVariant === 'preorder' ? <Clock className="mr-2 h-5 w-5" /> : <Bell className="mr-2 h-5 w-5" />}
        {buttonText}
      </Button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" data-testid="stock-alert-modal">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-slide-up">
            <button 
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              data-testid="stock-alert-close"
            >
              <X className="h-6 w-6" />
            </button>

            {submitted ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-[#E0F2F1] rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-[#3A5A40]" />
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: 'Manrope' }}>
                  You're on the list!
                </h3>
                <p className="text-gray-600 mb-6">
                  {successMessage} <strong>{product.name}</strong> {alertVariant === 'preorder' ? '' : 'is back in stock'}.
                </p>
                <Button
                  onClick={() => setIsOpen(false)}
                  className="bg-[#3A5A40] hover:bg-[#2A4030] text-white"
                >
                  Got it
                </Button>
              </div>
            ) : (
              <>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  alertVariant === 'preorder' ? 'bg-blue-100' : 'bg-[#FEE2E2]'
                }`}>
                  {alertVariant === 'preorder' 
                    ? <Clock className="h-6 w-6 text-[#003456]" />
                    : <Bell className="h-6 w-6 text-[#BC4749]" />
                  }
                </div>

                <h3 className="text-xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: 'Manrope' }}>
                  {modalTitle}
                </h3>
                <p className="text-gray-600 mb-6">
                  {modalDescription} <strong>{product.name}</strong>.
                </p>

                {/* Notification Type Toggle */}
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setNotificationType('restock')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      notificationType === 'restock'
                        ? 'bg-[#003456] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Back in Stock
                  </button>
                  <button
                    type="button"
                    onClick={() => setNotificationType('preorder')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      notificationType === 'preorder'
                        ? 'bg-[#003456] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Pre-Order Alert
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name (optional)"
                    className="w-full"
                    data-testid="stock-alert-name"
                  />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                    className="w-full"
                    data-testid="stock-alert-email"
                  />
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-[#3A5A40] hover:bg-[#2A4030] text-white py-3"
                    data-testid="stock-alert-submit"
                  >
                    {loading ? 'Submitting...' : 'Notify Me'}
                  </Button>
                </form>

                <p className="text-xs text-gray-500 mt-4 text-center">
                  We'll only email you about this product.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StockAlert;
